import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import AdminDashboard from "@/pages/AdminDashboard";
import Planteles from "@/pages/Planteles";
import Grupos from "@/pages/Grupos";
import Reportes from "@/pages/Reportes";
import TeacherDashboard from "@/pages/TeacherDashboard";
import Alumnos from "@/pages/Alumnos";
import DatosAlumnos from "@/pages/DatosAlumnos";
import DatosGenerales from "@/pages/DatosGenerales";
import DatosGeneralesIndex from "@/pages/DatosGeneralesIndex";
import Sectores from "@/pages/Sectores";
import Zonas from "@/pages/Zonas";
import Usuarios from "@/pages/Usuarios";
import MiPerfil from "@/pages/MiPerfil";
import IniciarCiclo from "@/pages/IniciarCiclo";
import Boletas from "@/pages/Boletas";
import BoletaPrint from "@/pages/BoletaPrint";
import PersonalPorFuncion from "@/pages/PersonalPorFuncion";
import PersonalEscuela from "@/pages/PersonalEscuela";
import LogoPlantel from "@/pages/LogoPlantel";
import Credenciales from "@/pages/Credenciales";
import CredencialesPrint from "@/pages/CredencialesPrint";
import Constancias from "@/pages/Constancias";
import ConstanciaPrint from "@/pages/ConstanciaPrint";
import Verificar from "@/pages/Verificar";
import AlumnosLista from "@/pages/AlumnosLista";
import Bajas from "@/pages/Bajas";
import HistorialBajas from "@/pages/HistorialBajas";
import MovimientosAlumnos from "@/pages/MovimientosAlumnos";
import Manual from "@/pages/Manual";
import DatosEstadisticosGrupo from "@/pages/DatosEstadisticosGrupo";
import Reconocimientos from "@/pages/Reconocimientos";
import ReconocimientoPrint from "@/pages/ReconocimientoPrint";
import Diplomas from "@/pages/Diplomas";
import DiplomaPrint from "@/pages/DiplomaPrint";
import MiPersonal from "@/pages/MiPersonal";
import MiPersonalPrint from "@/pages/MiPersonalPrint";

const NON_DOCENTE = ["superadmin", "jefe_sector", "supervisor_zona", "director"];

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="text-sm text-[#545454]">Cargando…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "docente" ? "/docente" : "/admin"} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/verificar" element={<Verificar />} />

          <Route path="/admin" element={<ProtectedRoute roles={NON_DOCENTE}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/sectores" element={<ProtectedRoute roles={["superadmin", "jefe_sector"]}><Sectores /></ProtectedRoute>} />
          <Route path="/admin/zonas" element={<ProtectedRoute roles={["superadmin", "jefe_sector", "supervisor_zona"]}><Zonas /></ProtectedRoute>} />
          <Route path="/admin/usuarios" element={<ProtectedRoute roles={NON_DOCENTE}><Usuarios /></ProtectedRoute>} />
          <Route path="/admin/planteles" element={<ProtectedRoute roles={NON_DOCENTE}><Planteles /></ProtectedRoute>} />
          <Route path="/admin/grupos" element={<ProtectedRoute roles={["superadmin", "director"]}><Grupos /></ProtectedRoute>} />
          <Route path="/admin/iniciar-ciclo" element={<ProtectedRoute roles={["superadmin", "director"]}><IniciarCiclo /></ProtectedRoute>} />
          <Route path="/admin/personal-funcion" element={<ProtectedRoute roles={["superadmin", "director"]}><PersonalPorFuncion /></ProtectedRoute>} />
          <Route path="/admin/personal-escuela" element={<ProtectedRoute roles={["superadmin", "director", "jefe_sector", "supervisor_zona"]}><PersonalEscuela /></ProtectedRoute>} />
          <Route path="/admin/logo-plantel" element={<ProtectedRoute roles={["superadmin", "director"]}><LogoPlantel /></ProtectedRoute>} />
          <Route path="/admin/credenciales" element={<ProtectedRoute roles={["superadmin", "director"]}><Credenciales /></ProtectedRoute>} />
          <Route path="/credenciales/print" element={<ProtectedRoute roles={["superadmin", "director"]}><CredencialesPrint /></ProtectedRoute>} />
          <Route path="/admin/constancias" element={<ProtectedRoute roles={["superadmin", "director"]}><Constancias /></ProtectedRoute>} />
          <Route path="/constancias/print" element={<ProtectedRoute roles={["superadmin", "director"]}><ConstanciaPrint /></ProtectedRoute>} />
          <Route path="/admin/alumnos-lista" element={<ProtectedRoute roles={NON_DOCENTE}><AlumnosLista /></ProtectedRoute>} />
          <Route path="/admin/bajas" element={<ProtectedRoute roles={["superadmin", "director"]}><Bajas /></ProtectedRoute>} />
          <Route path="/admin/historial-bajas" element={<ProtectedRoute roles={NON_DOCENTE}><HistorialBajas /></ProtectedRoute>} />
          <Route path="/admin/movimientos-alumnos" element={<ProtectedRoute roles={NON_DOCENTE}><MovimientosAlumnos /></ProtectedRoute>} />
          <Route path="/admin/manual" element={<ProtectedRoute roles={["superadmin", "jefe_sector", "supervisor_zona", "director", "docente"]}><Manual /></ProtectedRoute>} />
          <Route path="/manual" element={<ProtectedRoute roles={["superadmin", "jefe_sector", "supervisor_zona", "director", "docente"]}><Manual /></ProtectedRoute>} />
          <Route path="/grupos/:grupoId/datos-estadisticos" element={<ProtectedRoute roles={["superadmin", "director", "docente"]}><DatosEstadisticosGrupo /></ProtectedRoute>} />
          <Route path="/admin/reconocimientos" element={<ProtectedRoute roles={["superadmin", "director"]}><Reconocimientos /></ProtectedRoute>} />
          <Route path="/reconocimientos/print" element={<ProtectedRoute roles={["superadmin", "director"]}><ReconocimientoPrint /></ProtectedRoute>} />
          <Route path="/docente/diplomas" element={<ProtectedRoute roles={["superadmin", "director", "docente"]}><Diplomas /></ProtectedRoute>} />
          <Route path="/admin/diplomas" element={<ProtectedRoute roles={["superadmin", "director", "docente"]}><Diplomas /></ProtectedRoute>} />
          <Route path="/diplomas/print" element={<ProtectedRoute roles={["superadmin", "director", "docente"]}><DiplomaPrint /></ProtectedRoute>} />
          <Route path="/admin/perfil" element={<ProtectedRoute roles={NON_DOCENTE}><MiPerfil /></ProtectedRoute>} />
          <Route path="/admin/reportes" element={<ProtectedRoute roles={NON_DOCENTE}><Reportes /></ProtectedRoute>} />
          <Route path="/admin/mi-personal" element={<ProtectedRoute roles={["supervisor_zona", "jefe_sector", "superadmin"]}><MiPersonal /></ProtectedRoute>} />
          <Route path="/admin/mi-personal/print" element={<ProtectedRoute roles={["supervisor_zona", "jefe_sector", "superadmin"]}><MiPersonalPrint /></ProtectedRoute>} />

          <Route path="/docente" element={<ProtectedRoute roles={["docente"]}><TeacherDashboard /></ProtectedRoute>} />
          <Route path="/docente/grupos" element={<ProtectedRoute roles={["docente"]}><Grupos /></ProtectedRoute>} />
          <Route path="/docente/datos-generales" element={<ProtectedRoute roles={["docente"]}><DatosGeneralesIndex /></ProtectedRoute>} />
          <Route path="/perfil" element={<ProtectedRoute><MiPerfil /></ProtectedRoute>} />
          <Route path="/grupos/:grupoId/alumnos" element={<ProtectedRoute><Alumnos /></ProtectedRoute>} />
          <Route path="/grupos/:grupoId/boletas" element={<ProtectedRoute roles={["docente", "director", "superadmin"]}><Boletas /></ProtectedRoute>} />
          <Route path="/grupos/:grupoId/boletas/print" element={<ProtectedRoute roles={["docente", "director", "superadmin"]}><BoletaPrint /></ProtectedRoute>} />
          <Route path="/grupos/:grupoId/datos-alumnos" element={<ProtectedRoute><DatosAlumnos /></ProtectedRoute>} />
          <Route path="/grupos/:grupoId/datos-generales" element={<ProtectedRoute><DatosGenerales /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
